import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { format, isSameDay } from "date-fns";
import Loader from "../components/Loader";
import { getStatusColor, readableOn, REPORT_CELL_COLORS } from "../utils/badgeColors";
import {
  userReportListsApis,
  staffAttendanceApis,
  clientQueryStatusApis,
  reportListsApis,
} from "../redux/report/reportActions";
import DatePicker from "react-datepicker";
import ValueFilterDropdown from "../components/ValueFilterDropdown";
import { DATE_PRESETS } from "../utils/datePresets";
import "react-datepicker/dist/react-datepicker.css";
import DownloadProgressModal from "../components/DownloadProgressModal";
import Modal from "../components/Modal";
import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";
import ReportViewer from "../components/ReportViewer";
import FilterPanel from "../components/FilterPanel";

import { exportToPDF } from "../utils/pdfExport";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/reportTable.css";
import "../assets/css/reportSetup.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
  calculateDuration,
  displayStatus,
  formatDateOnly,
  formatDateTimeDisplay,
  isActiveUser,
  isPendingStatus,
} from "../utils/common";
import { matchesAdvancedFilters } from "../utils/filterUtils";
import { usersApis } from "../redux/users/userActions";
import { selectUsers } from "../redux/users/userSelectors";
import moment from "moment-timezone";

import { useTabRefresh } from "../context/TabsContext";
import { historyListAPIs } from "../redux/tasks/taskActions";
import {
  selectHistoryApiList,
  selectHistoryApiApiListStatus,
} from "../redux/tasks/taskSelectors";

// --- HELPER: Generate Unique Color from Name ---
const stringToColor = (string) => {
  if (!string || string === "All") return "#000";

  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 40%)`;
};

// --- HELPER: Convert Minutes to HH:MM Format ---
const minutesToHHMM = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === "")
    return "00:00";
  const m = Number(minutes);
  if (isNaN(m)) return "00:00";

  const hrs = Math.floor(m / 60);
  const mins = Math.floor(m % 60);

  const hrsStr = String(hrs).padStart(2, "0");
  const minsStr = String(mins).padStart(2, "0");

  return `${hrsStr}:${minsStr}`;
};

// --- HELPER: Timeline Colors ---
const getTimelineColor = (status) => {
  switch (status) {
    case "Complete":
    case "Completed":
      return "#2e7d32";
    case "Testing":
      return "#00838f";
    case "In Progress":
      return "#0d6efd";
    case "On Hold":
      return "#f57c00";
    case "Reassign":
    case "Not Possible":
      return "#d32f2f";
    default:
      return "#6c757d";
  }
};

// --- CUSTOM SINGLE-SELECT USER DROPDOWN (RADIO BUTTONS) ---
const UserFilterDropdown = ({
  usersList,
  selectedUsers,
  onChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  const options = useMemo(() => ["All", ...usersList], [usersList]);

  // Type to narrow the list — "All" always stays reachable.
  const visibleOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (o) => o === "All" || String(o).toLowerCase().includes(term)
    );
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* Several people at once.
   *
   * It was a radio list, so comparing two members of a team meant running the
   * report twice and holding the first one in your head. The array it reports
   * through always allowed for more than one — every reader downstream already
   * matches with `.includes()` — so only the picker itself was single.
   *
   * "All" is not one of the choices, it is the absence of them: picking it
   * clears the rest, and picking anybody else clears it. Deselecting the last
   * name falls back to All rather than leaving a filter that matches nothing
   * and an empty report with no explanation. */
  const toggleSelection = (option) => {
    if (option === "All") {
      onChange(["All"]);
      return;
    }

    const current = selectedUsers.filter((u) => u !== "All");
    const next = current.includes(option)
      ? current.filter((u) => u !== option)
      : [...current, option];

    onChange(next.length ? next : ["All"]);
    /* The menu stays open — a multi-select that closes on every pick makes
       choosing three people three round trips. Outside-click closes it. */
  };

  const getLabel = () => {
    if (selectedUsers.includes("All") || selectedUsers.length === 0)
      return "All Users";
    if (selectedUsers.length === 1) return selectedUsers[0];
    return `${selectedUsers.length} users selected`;
  };

  return (
    <div ref={wrapperRef} className={`position-relative ${className || ""}`}>
      <button
        className="btn btn-white border d-flex align-items-center justify-content-between shadow-sm w-100"
        style={{ height: "40px", fontSize: "0.85rem", fontWeight: 600, color: "#495057", borderRadius: "8px", backgroundColor: "#fff", minWidth: "160px" }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center gap-2 text-truncate">
          <i className="bi bi-person-fill text-primary opacity-75"></i>
          <span>{getLabel()}</span>
        </div>
        <i className="bi bi-chevron-down ms-2 text-muted" style={{ fontSize: "0.7rem" }}></i>
      </button>

      {isOpen && (
        <div
          className="shadow-lg border-0 rounded-3 bg-white py-2"
          style={{ position: "absolute", top: "115%", left: 0, width: "250", minWidth: "200px", maxHeight: "300px", overflowY: "auto", zIndex: 1000, animation: "fadeIn 0.15s ease-out" }}
        >
          <div className="px-2 pb-2 sticky-top bg-white">
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder="Search user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: "0.8rem", height: "32px" }}
            />
          </div>

          {visibleOptions.length === 0 && (
            <div className="px-3 py-2 text-muted" style={{ fontSize: "0.8rem" }}>
              No users found
            </div>
          )}

          {visibleOptions.map((option) => (
            <div
              key={option}
              className="px-3 py-2 d-flex align-items-center gap-2 dropdown-item-custom"
              style={{ cursor: "pointer", transition: "all 0.2s" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(option);
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <input
                type="checkbox"
                className="form-check-input"
                checked={
                  option === "All"
                    ? selectedUsers.includes("All") || selectedUsers.length === 0
                    : selectedUsers.includes(option)
                }
                readOnly
                style={{ cursor: "pointer" }}
              />
              <div
                className="d-flex align-items-center justify-content-center text-white"
                style={{ width: "24px", height: "24px", borderRadius: "50%", backgroundColor: stringToColor(option), fontSize: "0.65rem", fontWeight: "bold", flexShrink: 0 }}
              >
                {option === "All" ? <i className="bi bi-list"></i> : option.charAt(0).toUpperCase()}
              </div>
              <span
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  color: "#333",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1
                }}
              >
                {option}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* The same report types the Overall Report offers, cut to what this data
   actually carries. sp_Sky_DashBoard_User_WorkingHistory has no Project or
   Query Type, so offering them would give a report of one "(none)" group. */
/* The two ways of reading any of the report types below. */
const SCOPES = [
  { key: "all", label: "All", icon: "bi-collection" },
  { key: "pending", label: "Pending", icon: "bi-hourglass-split" },
];

const REPORT_TYPES = [
  { key: "none", label: "Detailed (No Grouping)", icon: "bi-list-ul", tone: "grey" },
  { key: "UserName", label: "User Wise", icon: "bi-person-badge", tone: "purple" },
  { key: "ShortName", label: "Client Wise", icon: "bi-building", tone: "blue" },
  { key: "Status", label: "Status Wise", icon: "bi-flag", tone: "green" },
  { key: "TeamName", label: "Team Wise", icon: "bi-people", tone: "orange" },
  {
    key: "StartTime",
    label: "Date Wise",
    icon: "bi-calendar3",
    tone: "pink",
    /* By day, not by timestamp — grouping on the raw StartTime would put
       every single row in a group of its own. */
    value: (r) => formatDateTimeDisplay(r.StartTime, "DD/MM/YYYY", "(no date)"),
  },
  { key: "FormName", label: "Menu Wise", icon: "bi-list-nested", tone: "teal" },
  /* Development vs QA, with the per-group totals that come with any grouping —
     which is the quickest way to answer "how much of the week went on
     testing", a question the report could not answer at all until QA time
     started appearing in it. */
  {
    key: "WorkType",
    label: "Work Wise",
    icon: "bi-shield-check",
    tone: "green",
    value: (r) =>
      r.ChangeStatus === "Testing In Progress" ? "QA" : "Development",
  },
  /* Not a grouping like the others — a different report on the same rows, in
     the layout the team leads already circulate by hand. `columns: "tl"`
     switches the column set; see tlColumns. */
  {
    key: "tl",
    label: "TL Report",
    icon: "bi-clipboard-data",
    tone: "indigo",
    columns: "tl",
  },
  /* The support workbook's first table, on screen instead of in a download.
   *
   * It was a button that produced an .xlsx and nothing else — you could not
   * see what you were about to send until you had opened it in Excel. The
   * table it builds is one column set grouped by staff with a subtotal per
   * person, which is exactly what this viewer already does, so it becomes a
   * report type like the rest: printable, exportable from the toolbar in any
   * of the four formats, and readable before any of that.
   *
   * The workbook's other three tables — Client Support & Complaints, Running
   * Project, Team Wise Pending — are summaries over other endpoints and have
   * their own column sets, so they are not in this. See downloadSupportTeamReport,
   * which still builds all four. */
  /* The Daily Management Report — three tables on one sheet, the format the
     team circulates by hand:

       1. Staff Attendance & Utilization
       2. Client Support & Complaints
       3. Client Query Status

     Not a grouping of this page's rows like the types above it, so it does
     not read the report's own data at all: each section has its own source,
     fetched when the report is opened. */
  {
    key: "support",
    label: "Support Team Report",
    icon: "bi-file-earmark-spreadsheet",
    tone: "green",
    sectioned: true,
  },
];

/* Section 1 — the attendance table, straight off sp_Sky_Report_StaffAttendance. */
const ATTENDANCE_COLUMNS = [
  { key: "UserName", label: "Name", width: "140px" },
  { key: "WorkedDays", label: "Total Worked Days", width: "110px", numeric: true },
  { key: "TodayPresent", label: "Today Present", width: "110px" },
  { key: "LeaveDays", label: "Total Leave", width: "90px", numeric: true },
  /* Hours worked on tasks this month, and that divided by the days worked.
     Both come from the procedure as HH:MM strings — it also returns
     WorkedMinutes and AvgMinutes for anything that needs to sort or total
     them without parsing a clock. */
  { key: "WorkingHours", label: "Working Hours", width: "94px", numeric: true },
  { key: "AvgHours", label: "Avg Hours / Day", width: "96px", numeric: true },
  { key: "Remarks", label: "Remarks", width: "200px" },
];

/* Section 2 — the open client work, one line per query. */
const COMPLAINT_COLUMNS = [
  {
    key: "ClientName",
    label: "Client Name",
    width: "110px",
    value: (r) => r.ClientName || r.CompanyName || "-",
  },
  { key: "QueryType", label: "Issue Type", width: "84px" },
  { key: "Priority", label: "Priority", width: "72px" },
  {
    key: "isUpdate",
    label: "Update Sts",
    width: "80px",
    value: (r) => r.IsUpdate || r.isUpdate || "-",
  },
  { key: "Status", label: "Status", width: "92px" },
  {
    key: "NatureofQuery",
    label: "Points Details",
    width: "420px",
    value: (r) => r.NatureofQuery || r.TaskName || r.Description || "-",
  },
];

/* Section 3 — per client, with ageing. The column labels match the procedure's
   own vocabulary so the sheet and the query agree on what "Opening" means. */
const QUERY_STATUS_COLUMNS = [
  { key: "Client", label: "Client", width: "110px" },
  { key: "Opening", label: "Opening", width: "70px", numeric: true },
  { key: "TodayRaised", label: "Today Raised", width: "82px", numeric: true },
  { key: "NotPossible", label: "Not Possible", width: "82px", numeric: true },
  /* Today's completions, not the lifetime total. Labelled to match "Today
     Raised" beside it, so the pair reads as the day's in and out. */
  { key: "Completed", label: "Today Completed", width: "88px", numeric: true },
  /* Pending, split by where the work came from, then the total the two make.
     The total is printed after its parts rather than before them: the reader
     checks the split against it, and a total met first is a number you have to
     hold in your head while reading the two beside it. */
  { key: "PendingClient", label: "Pending (Client)", width: "88px", numeric: true },
  { key: "PendingOwn", label: "Pending (Own)", width: "82px", numeric: true },
  { key: "Pending", label: "Total Pending", width: "84px", numeric: true },
  { key: "OnHold", label: "On Hold", width: "70px", numeric: true },
  { key: "NotStarted", label: "Not Started", width: "78px", numeric: true },
  { key: "InProgress", label: "In Progress", width: "78px", numeric: true },
  { key: "Testing", label: "Testing", width: "70px", numeric: true },
  { key: "AgingOver", label: "Aging >2d", width: "74px", numeric: true },
  {
    key: "OldestDays",
    label: "Oldest",
    width: "66px",
    numeric: true,
    value: (r) => (r.OldestDays ? `${r.OldestDays}d` : "0d"),
  },
  {
    key: "AvgAgeDays",
    label: "Avg Age",
    width: "70px",
    numeric: true,
    value: (r) => (r.AvgAgeDays ? `${r.AvgAgeDays}d` : "0d"),
  },
];


/* The attribute filters — Client, Team, Work, Menu.
 *
 * One list rather than four copies of the same JSX, because they differ only
 * in which field they read and what to call it. `field` is the row key, and
 * the options are read off the loaded rows rather than a master table: a
 * client with no work in the window is not a choice worth offering, and a
 * value that appears in the report but not in the master would otherwise be
 * unfilterable.
 *
 * `value` where the field is derived rather than stored — Work is QA or
 * Development depending on ChangeStatus, exactly as the printed column
 * computes it, so filtering matches what the reader can see. */
const ATTRIBUTE_FILTERS = [
  { key: "client", label: "Client", plural: "clients", icon: "bi-building", field: "ShortName" },
  { key: "team", label: "Team", plural: "teams", icon: "bi-people", field: "TeamName" },
  {
    key: "work",
    label: "Work",
    plural: "work types",
    icon: "bi-shield-check",
    field: "WorkType",
    value: (r) =>
      r.ChangeStatus === "Testing In Progress" ? "QA" : "Development",
  },
  { key: "menu", label: "Menu", plural: "menus", icon: "bi-list-nested", field: "FormName" },
  /* Status sits with the columns rather than beside Scope, even though the
     two overlap — Scope asks the one question this report is built around
     ("is it still outstanding?") and answers it with a count on the control
     itself, while this picks named statuses the way the other columns pick
     named values. Both left alone, they do not fight: the scope narrows
     first, then this narrows what is left. */
  { key: "status", label: "Status", plural: "statuses", icon: "bi-flag", field: "Status" },
  /* Reaches the rows only once 2026-08-20_user_history_priority.sql has been
     applied — the procedure behind this report did not select the field. The
     filter is harmless before then: with nothing to offer, it lists nothing
     and narrows nothing. */
  { key: "priority", label: "Priority", plural: "priorities", icon: "bi-flag-fill", field: "Priority" },
];

/* A multi-select over one column's values. The user picker above is the same
   control with avatars and a users master behind it; this one takes whatever
   list it is handed.


// --- MAIN REPORTS COMPONENT ---
/* The Status cell, tinted the way the Over All Report tints its own — a status
   that is a colour in one report and plain text in the other is two things to
   learn. It also stops Status reading like just another free-text column next
   to the Description, which is exactly how it was being misread. */
const priorityTint = (value, row) => {
  const fn = REPORT_CELL_COLORS.Priority;
  if (!fn) return undefined;
  const c = readableOn(fn(value, row));
  if (!c || !c.bg || c.bg === "transparent") return undefined;
  return { backgroundColor: c.bg, color: c.text, fontWeight: 600 };
};

const statusTint = (value, row) => {
  const fn = REPORT_CELL_COLORS.Status;
  if (!fn) return undefined;
  const c = readableOn(fn(value, row));
  if (!c || !c.bg || c.bg === "transparent") return undefined;
  return { backgroundColor: c.bg, color: c.text, fontWeight: 600 };
};

const UserReports = () => {
  const dispatch = useDispatch();

  // Selectors — this screen has its OWN slice keys so the Overall Report
  // (which shares the `report` slice) can never overwrite this grid.
  const reportListData = useSelector((state) => state.report.userReportListData);
  const status = useSelector((state) => state.report.userReportStatus);
  const usersData = useSelector(selectUsers);

  const historyData = useSelector(selectHistoryApiList);
  const historyStatus = useSelector(selectHistoryApiApiListStatus);

  // State
  const [userFilter, setUserFilter] = useState(["All"]);
  /* One entry per ATTRIBUTE_FILTERS key, all starting at "All". Held as a
     single object rather than four useStates so adding a fifth column to
     filter on is a line in the list above and nothing here. */
  const [attrFilters, setAttrFilters] = useState(() =>
    Object.fromEntries(ATTRIBUTE_FILTERS.map((f) => [f.key, ["All"]])),
  );
  const setAttrFilter = (key, values) =>
    setAttrFilters((prev) => ({ ...prev, [key]: values }));
  const [filterList, setFilterList] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const [reportType, setReportType] = useState("UserName");
  const [reportScope, setReportScope] = useState("all");

  /* Setup, then report. Two screens rather than one.
   *
   * The controls and the document were sharing a screen, and the document
   * won: it takes the whole height, so the controls got a single row above
   * it and had to be shrunk until a report type was a menu you opened to see
   * what was in it. Separated, each gets the room it needs — and the choices
   * are made once, deliberately, instead of being nudged one at a time while
   * a 28-page render redraws underneath.
   *
   * Which panels are folded away is remembered per section, because somebody
   * who never uses the advanced filters should not have to look at them. */
  const [view, setView] = useState("setup");
  const [showFilters, setShowFilters] = useState(true);
  const [showTypes, setShowTypes] = useState(true);
  /* Which chip on the report's context bar has its menu down: "date", "type",
     or nothing. One value rather than a flag each, because opening the second
     has to shut the first — two menus over one bar is two answers to the same
     question. */
  const [chipMenu, setChipMenu] = useState(null);
  const chipsRef = useRef(null);

  /* mousedown rather than click: a menu that closes on click-up is still on
     screen while the press lands underneath it, and the reader sees the thing
     they were aiming at move. */
  useEffect(() => {
    if (!chipMenu) return undefined;
    const onDown = (e) => {
      if (chipsRef.current && !chipsRef.current.contains(e.target)) {
        setChipMenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [chipMenu]);

  const activeType = useMemo(
    () => REPORT_TYPES.find((t) => t.key === reportType) ?? REPORT_TYPES[0],
    [reportType],
  );

  /* Opens on today.
   *
   * It was the last seven days for a while, on the reasoning that a day-wide
   * window looks empty first thing in the morning. That reasoning is weaker
   * now the presets are on the screen: Today is lit when you arrive, the six
   * other periods are one click away in the same row, and an empty report
   * under a button reading Today explains itself — where an empty report over
   * an unnamed seven-day span does not.
   *
   * DATE_PRESETS[0] rather than a second `new Date()` here, so the default
   * and the button that reproduces it can never drift apart. */
  const [dateRange, setDateRange] = useState(() => DATE_PRESETS[0].range());
  /* Driven by the data grid that used to be on this page: the rows it had
     narrowed to, the columns left visible, and its per-column filters. With
     the grid gone they never change, so they are constants rather than state
     nothing can set. Every reader already falls back when they are empty. */
  const finalExportData = null;
  const visibleColumns = [];
  const tableFilters = [];

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedHistoryTask, setSelectedHistoryTask] = useState(null);

  const lastFetchedParams = useRef("");
  // const usersRequestedRef = useRef(false);

  const windowScrollRef = useRef(0);
  const tableScrollRef = useRef(0);


  // NOTE: Working / Idle totals are derived from the rows actually shown (see
  // `summaryTotals` below), not from the server's whole-response total — so they
  // always reflect the current employee / date filter.

  useEffect(() => {
    dispatch(
      usersApis({
        currentPage: 1,
        pageSize: 1000,
      })
    );
  }, [dispatch]);

  const usersArray = useMemo(() => {
    const data =
      usersData?.data?.data ||
      usersData?.data ||
      usersData;

    return Array.isArray(data) ? data : [];
  }, [usersData]);

  /* Names for the user filter — active accounts only. /user/list returns every
     account because the Users screen needs the inactive ones; a filter offering
     somebody who has been switched off is just a way to get an empty report.
     Their historical rows still carry their name, which is correct. */
  const usersList = useMemo(() => {
    return usersArray
      .filter(isActiveUser)
      .map((u) => u?.UserName)
      .filter(Boolean);
  }, [usersArray]);

  console.log("USERS DATA:", usersData);
  console.log("USERS ARRAY:", usersArray);
  console.log("USERS LIST:", usersList);

  const selectedUserCode = useMemo(() => {
    if (userFilter.includes("All") || userFilter.length === 0) return null;
    /* More than one person selected: fetch everybody and narrow in the
       browser. The endpoint takes a single UserCode, so sending the first
       name's code would quietly return one person's rows and drop the others
       — a filter that asks for three and answers with one. userFilteredData
       already matches on the whole list, so nothing else has to change. */
    if (userFilter.length > 1) return null;
    // Look the code up from the SAME (defensively-shaped) users array the
    // dropdown is built from, and match names case/space-insensitively — so the
    // server reliably receives the UserCode instead of silently getting none.
    const target = String(userFilter[0] || "").trim().toLowerCase();
    const user = usersArray.find(
      (u) => String(u?.UserName || "").trim().toLowerCase() === target
    );
    return user ? user.UserCode : null;
  }, [userFilter, usersArray]);

  const handleRowClick = useCallback((row) => {
    windowScrollRef.current = window.scrollY || document.documentElement.scrollTop;

    const tableWrapper = document.querySelector(".table-responsive")
      || document.querySelector(".rt-tbody")
      || document.querySelector(".report-container table:not(.rv-table)")?.parentElement;

    if (tableWrapper) {
      tableScrollRef.current = tableWrapper.scrollTop;
    }

    setSelectedHistoryTask(row);
    setIsHistoryModalOpen(true);
  }, []);

  const closeHistoryModal = useCallback(() => {
    const savedWindowY = windowScrollRef.current;
    const savedTableY = tableScrollRef.current;

    setIsHistoryModalOpen(false);
    setSelectedHistoryTask(null);

    const enforceInstantScroll = () => {
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.scrollBehavior = "auto";
      window.scrollTo({ top: savedWindowY, left: 0, behavior: "auto" });

      const tableWrapper = document.querySelector(".table-responsive")
        || document.querySelector(".rt-tbody")
        || document.querySelector(".report-container table:not(.rv-table)")?.parentElement;

      if (tableWrapper) {
        tableWrapper.style.scrollBehavior = "auto";
        tableWrapper.scrollTop = savedTableY;
      }

      setTimeout(() => {
        document.documentElement.style.scrollBehavior = "";
        document.body.style.scrollBehavior = "";
        if (tableWrapper) tableWrapper.style.scrollBehavior = "";
      }, 10);
    };

    enforceInstantScroll();
    requestAnimationFrame(enforceInstantScroll);
  }, []);

  /* handleExportClickOpen was the grid's onExport hook and went with it —
     the viewer's Export button calls handleExportClick directly. */

  /* getRowClassName / handleTableDataChange / handleTableFilterChange /
     handleVisibleColumnsChange were the grid's callbacks and went with it —
     see the note by the export for what it falls back to. */

  const fetchReports = useCallback(
    (start, end, userCode) => {
      const payload = {
        fromDate: start ? format(start, "yyyy-MM-dd") : null,
        toDate: end ? format(end, "yyyy-MM-dd") : null,
      };
      if (userCode) payload.UserCode = userCode;
      dispatch(userReportListsApis(payload));
    },
    [dispatch],
  );

  const fromDateStr = dateRange.startDate ? format(dateRange.startDate, "yyyy-MM-dd") : "";
  const toDateStr = dateRange.endDate ? format(dateRange.endDate, "yyyy-MM-dd") : "";

  /* Pending Only ignores the date range, on purpose.
   *
   * A backlog is not a window. Filtering "still outstanding" down to the last
   * seven days answers a question nobody asks — the task raised in May and
   * never started is precisely the one worth seeing, and it is precisely the
   * one a date range hides. So the pending view fetches all time and the
   * dates stand down; the procedure reads NULL as "no limit" at each end.
   *
   * The scope is part of the fetch key, or switching back to All would keep
   * showing the all-time rows it had already loaded. */
  useEffect(() => {
    const allTime = reportScope === "pending";
    const currentParams = `${allTime ? "ALL-TIME" : `${fromDateStr}|${toDateStr}`}|${selectedUserCode || "ALL"}`;
    if (lastFetchedParams.current === currentParams) return;

    if (allTime) {
      lastFetchedParams.current = currentParams;
      fetchReports(null, null, selectedUserCode);
      return;
    }

    if (fromDateStr && toDateStr) {
      lastFetchedParams.current = currentParams;
      fetchReports(dateRange.startDate, dateRange.endDate, selectedUserCode);
    }
  }, [fetchReports, fromDateStr, toDateStr, dateRange.startDate, dateRange.endDate, selectedUserCode, reportScope]);

  // Re-pull on every tab (re)activation. The pane stays mounted while another
  // tab is on screen, so the effect above — keyed on the date/user params —
  // never fires again on its own; without this, coming back would show
  // whatever was last fetched, however old.
  useTabRefresh(() => {
    /* Same rule as the effect above — a refresh that re-applied the dates
       would silently narrow a pending view back to the window. */
    if (reportScope === "pending") {
      lastFetchedParams.current = `ALL-TIME|${selectedUserCode || "ALL"}`;
      fetchReports(null, null, selectedUserCode);
      return;
    }
    if (!fromDateStr || !toDateStr) return;
    lastFetchedParams.current = `${fromDateStr}|${toDateStr}|${selectedUserCode || "ALL"}`;
    fetchReports(dateRange.startDate, dateRange.endDate, selectedUserCode);
  });

  useEffect(() => {
    if (selectedHistoryTask && isHistoryModalOpen) {
      dispatch(historyListAPIs({ queryCode: selectedHistoryTask.QueryCode || selectedHistoryTask.QueryNo }));
    }
  }, [selectedHistoryTask, isHistoryModalOpen, dispatch]);

  const handleDateChange = (start, end) => {
    const currentStart = dateRange.startDate ? dateRange.startDate.getTime() : 0;
    const currentEnd = dateRange.endDate ? dateRange.endDate.getTime() : 0;
    const newStart = start ? start.getTime() : 0;
    const newEnd = end ? end.getTime() : 0;

    if (currentStart !== newStart || currentEnd !== newEnd) {
      setDateRange({ startDate: start, endDate: end });
    }
  };

  /* Which preset button is lit, if any. Derived rather than stored: the dates
     can also be typed into the two boxes, and a stored key would keep
     claiming "This Month" after somebody edited one end of it. */
  const activePreset = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return null;
    const hit = DATE_PRESETS.find((p) => {
      const r = p.range();
      return isSameDay(r.startDate, startDate) && isSameDay(r.endDate, endDate);
    });
    return hit ? hit.key : null;
  }, [dateRange]);

  const applyPreset = (preset) => {
    const r = preset.range();
    handleDateChange(r.startDate, r.endDate);
  };

  /* <input type="date"> hands back "2026-08-14". Parsed with an explicit
     midnight because `new Date("2026-08-14")` is read as UTC, which lands on
     the 13th anywhere west of Greenwich and shifts the whole report by a day. */
  const setDateEnd = (which) => (e) => {
    const v = e.target.value;
    const parsed = v ? new Date(`${v}T00:00:00`) : null;
    if (!parsed) return;
    handleDateChange(
      which === "start" ? parsed : dateRange.startDate,
      which === "end" ? parsed : dateRange.endDate,
    );
  };

  const dateInputValue = (d) => (d ? format(d, "yyyy-MM-dd") : "");

  /* react-datepicker hands back [start, null] on the first click of a range
     and [start, end] on the second. Passed straight through: a half-made
     range leaves endDate null, the fetch effect needs both ends and so sits
     still, and the second click completes it. Forcing end = start on the
     first click would make every range one day long and no two-day range
     selectable at all. */
  const handleCalendarRange = (dates) => {
    const [start, end] = Array.isArray(dates) ? dates : [dates, dates];
    handleDateChange(start, end);
  };

  /* "Clear" restores the window the page opens on rather than emptying the
     dates. With no dates there is nothing to fetch and the report would go
     blank with no way back except picking two days by hand. Reads the same
     preset the initial state does, so "Clear" and "reopen the page" always
     land on the same window. */
  const clearDates = () => {
    const r = DATE_PRESETS[0].range();
    handleDateChange(r.startDate, r.endDate);
  };

  /* What the context bar's date chip says. The preset name when one is lit,
     because "Yesterday" is the thing the reader chose and the date it stands
     for is on the line underneath. */
  const rangeLabel = useMemo(() => {
    if (reportScope === "pending") return "All time";
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return "—";
    if (isSameDay(startDate, endDate)) return format(startDate, "dd/MM/yyyy");
    return `${format(startDate, "dd/MM/yyyy")} – ${format(endDate, "dd/MM/yyyy")}`;
  }, [dateRange, reportScope]);

  const presetLabel = useMemo(() => {
    if (reportScope === "pending") return "Pending";
    const hit = DATE_PRESETS.find((p) => p.key === activePreset);
    return hit ? hit.label : "Custom range";
  }, [activePreset, reportScope]);

  // --- COLUMNS ---
  /* Printed layout. RunningMinutes and IdleMinutes are already minutes, so
     they total cleanly — unlike the "HH:MM" display columns, which cannot be
     added up. */
  /* Widths are real minimums — the sheet grows to hold them, so each is set
     to fit its widest ordinary value rather than being squeezed. */
  const printColumns = useMemo(
    () => [
      { key: "UserName", label: "User", width: "92px", value: (r) => r.UserName },
      { key: "ShortName", label: "Client", width: "92px", value: (r) => r.ShortName },
      /* From migration 12 — without it Team Wise would group everything
         under one "(none)". */
      { key: "TeamName", label: "Team", width: "76px", value: (r) => r.TeamName },
      {
        /* Development or QA.
         *
         * Both are now in this report — QA time used to be missing from it
         * entirely, because the procedure only counted 'In Progress' and the
         * QA ladder runs on 'Testing In Progress'. With both present, a row
         * has to be able to say which kind of work it was: an hour of testing
         * and an hour of building are not interchangeable to anybody reading
         * a person's day, and without this they look identical.
         *
         * Read from ChangeStatus — the status the segment STARTED in, which
         * is what defines the work — not from the task's current Status,
         * which has moved on since. */
        key: "WorkType",
        label: "Work",
        width: "78px",
        value: (r) =>
          r.ChangeStatus === "Testing In Progress" ? "QA" : "Development",
      },
      { key: "FormName", label: "Menu", width: "120px", value: (r) => r.FormName },
      { key: "NatureofQuery", label: "Nature of Query", width: "230px", value: (r) => r.NatureofQuery },
      /* Status sits immediately before the Description, and ahead of it.
         It used to be the twelfth column, behind Start, End, Is Update and
         Support — far enough right that on a normal screen the only thing
         readable near the middle of the row was the Description, which is
         free text somebody typed. Thirty-five pending rows carry a description
         reading "completed" while the task is actually On Hold, Testing or
         Not Started, so the report looked like Pending Only was broken when it
         was in fact reading the one field that means anything. The
         authoritative value now comes first, the note commenting on it after. */
      {
        key: "Status",
        label: "Status",
        width: "78px",
        value: (r) => displayStatus(r.Status),
        cellStyle: statusTint,
      },
      /* Same tint the Over All Report gives it, so High reads as High on
         both reports rather than as a colour on one and plain text on the
         other. A task with no priority prints as a dash: nobody set one,
         which is not the same as it being Medium. */
      {
        key: "Priority",
        label: "Priority",
        width: "74px",
        value: (r) => r.Priority || "-",
        cellStyle: priorityTint,
      },
      /* How many sittings the row merges. Blank at one, because "1" on almost
         every line is noise — it is only worth saying when a task was picked
         up and put down repeatedly, which is exactly when a single row
         spanning seven hours needs explaining. */
      {
        key: "Sessions",
        label: "Sittings",
        width: "58px",
        numeric: true,
        value: (r) => (r.Sessions > 1 ? r.Sessions : "-"),
      },
      { key: "Description", label: "Description", width: "180px", value: (r) => r.Description },
      {
        key: "StartTime",
        label: "Start Time",
        width: "104px",
        /* Read through the shared formatter, not plain moment — see
           formatDateTimeDisplay in utils/common. */
        value: (r) => formatDateTimeDisplay(r.StartTime, "DD/MM/YY hh:mm A", ""),
      },
      {
        key: "EndTime",
        label: "End Time",
        width: "104px",
        value: (r) => formatDateTimeDisplay(r.EndTime, "DD/MM/YY hh:mm A", ""),
      },
      { key: "IsUpdate", label: "Is Update", width: "80px", value: (r) => r.IsUpdate || r.isUpdate },
      {
        key: "IsSupportTeam",
        label: "Support",
        width: "58px",
        value: (r) => (r.IsSupportTeam ? "Yes" : "No"),
      },
      /* Estimate, actual, and the gap between them, side by side at the end of
         the row. Est. Time used to sit six columns to the left, next to the
         Description, which meant comparing what a task was expected to take
         against what it did take involved reading across half a page. They are
         one thought, so they are now one block.

         Minutes, not the "HH:MM" display strings — those cannot be summed, and
         a per-person total is the point of this report. */
      {
        /* Beside the estimate, because they are the same thought from two
           ends: how long it was expected to take, and when it was wanted by.

           Blank rather than a dash when unset — this prints, and a column of
           dashes down a page reads as a fault in the report rather than as a
           date nobody set. Formatted through formatDateOnly for the reason
           that helper exists: a SQL DATE arrives as an instant and slicing it
           loses a day. */
        key: "TargetDate",
        label: "Target Date",
        width: "82px",
        value: (r) => formatDateOnly(r.TargetDate, ""),
      },
      {
        /* Once per task, on the row where its last stretch of work ends —
           see rowsWithTaskTotals. Blank on a continuation row, because the
           estimate was not made again when the task was picked back up. */
        key: "EstimateTime",
        label: "Est. Time",
        width: "70px",
        total: true,
        kind: "duration",
        value: (r) => r.TaskEstimate,
      },
      {
        key: "RunningMinutes",
        label: "Worked",
        width: "68px",
        total: true,
        kind: "duration",
        value: (r) => r.RunningMinutes || 0,
      },
      {
        /* Estimate minus everything actually spent on the task, across every
           sitting: positive means it came in under, negative is an overrun.
           Sits on the same row as the estimate it is measured against.

           Same definition as the TL Report's column of the same name, so the
           two reports cannot tell different stories about the same task. */
        key: "DiffTime",
        label: "Different Time",
        width: "78px",
        total: true,
        kind: "duration",
        value: (r) => r.TaskDiff,
      },
      {
        key: "IdleMinutes",
        label: "Idle",
        width: "62px",
        total: true,
        kind: "duration",
        value: (r) => r.IdleMinutes || 0,
      },
    ],
    [],
  );

  /* The TL Report — the sheet the team leads already circulate, as its own
     column set rather than a second page.

     "Different time" is estimate minus actual, so a positive number is time
     saved against the estimate and a negative one is an overrun. That is the
     column a lead reads first, and it is why the report exists; showing worked
     time again next to Comp. Time would just repeat a number already on the
     row. Kept in MINUTES with kind:"duration" so the viewer can sum it — a
     pre-formatted "HH:MM" string cannot carry a total. */
  const tlColumns = useMemo(
    () => [
      {
        key: "TLDate",
        label: "Date",
        width: "76px",
        value: (r) => formatDateTimeDisplay(r.StartTime, "DD.MM.YYYY", ""),
      },
      { key: "ShortName", label: "Project / Client", width: "104px", value: (r) => r.ShortName },
      { key: "FormName", label: "Module / Menu", width: "116px", value: (r) => r.FormName },
      { key: "NatureofQuery", label: "Task", width: "230px", value: (r) => r.NatureofQuery },
      { key: "UserName", label: "Assigned Staff", width: "100px", value: (r) => r.UserName },
      {
        /* Beside the estimate, because they are the same thought from two
           ends: how long it was expected to take, and when it was wanted by.

           Blank rather than a dash when unset — this prints, and a column of
           dashes down a page reads as a fault in the report rather than as a
           date nobody set. Formatted through formatDateOnly for the reason
           that helper exists: a SQL DATE arrives as an instant and slicing it
           loses a day. */
        key: "TargetDate",
        label: "Target Date",
        width: "82px",
        value: (r) => formatDateOnly(r.TargetDate, ""),
      },
      {
        key: "EstimateTime",
        label: "Estimate Time",
        width: "80px",
        total: true,
        kind: "duration",
        /* Once per task, like the main sheet — see rowsWithTaskTotals. */
        value: (r) => r.TaskEstimate,
      },
      {
        key: "RunningMinutes",
        label: "Comp. Time",
        width: "78px",
        total: true,
        kind: "duration",
        value: (r) => r.RunningMinutes || 0,
      },
      {
        key: "TLDiff",
        label: "Different time",
        width: "84px",
        total: true,
        kind: "duration",
        /* The estimate against everything spent on the task, not against one
           sitting at it — see rowsWithTaskTotals. */
        value: (r) => r.TaskDiff,
      },
      { key: "Status", label: "Status", width: "82px", value: (r) => displayStatus(r.Status) },
      { key: "Description", label: "Reason", width: "180px", value: (r) => r.Description },
    ],
    [],
  );

  /* The workbook's first table, column for column: what was promised, when it
     began, when it ended, what it cost, and why. Left to right it tells the
     job in order, which is the layout the sheet was built around. */


  const columns = useMemo(
    () => [
      {
        label: "Assignee Name",
        key: "UserName",
        width: "250px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div className="text-primary fw-medium text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.UserName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.UserName || "-"}
          </div>
        ),
      },
      {
        label: "Client",
        key: "ShortName",
        width: "130px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div className="fw-bold text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.ShortName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.ShortName || "-"}
          </div>
        ),
      },
      {
        label: "Menu",
        key: "FormName",
        width: "140px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.FormName} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.FormName || "-"}
          </div>
        ),
      },
      {
        label: "Nature of Query",
        key: "NatureofQuery",
        width: "250px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.NatureofQuery} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.NatureofQuery || "-"}
          </div>
        ),
      },
      {
        label: "Est. Time",
        key: "EstimateTime",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {minutesToHHMM(r.EstimateTime)}
          </div>
        ),
      },
      {
        label: "Start Time",
        key: "StartTime",
        width: "150px",
        type: "datetime",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {formatDateTimeDisplay(r.StartTime)}
          </div>
        ),
      },
      {
        label: "End Time",
        key: "EndTime",
        width: "150px",
        type: "datetime",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {formatDateTimeDisplay(r.EndTime)}
          </div>
        ),
      },
      {
        label: "Time Taken",
        key: "RunningMinutes",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {minutesToHHMM(r.RunningMinutes)}
          </div>
        ),
      },
      {
        label: "Idle Time",
        key: "IdleMinutes",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {minutesToHHMM(r.IdleMinutes)}
          </div>
        ),
      },
      {
        label: "Is Update",
        key: "IsUpdate",
        type: "select",
        filterType: "select",
        options: ["None", "Backend work", "Update", "Not update"],
        searchValue: (r) => r.IsUpdate || r.isUpdate || "None",
        render: (r) => {
          const val = r.IsUpdate || r.isUpdate || "None";
          const lowerVal = String(val).toLowerCase();

          let bg = "#f8f9fa";
          let color = "#6c757d";

          if (lowerVal === "backend work") {
            bg = "#f3e5f5";
            color = "#7b1fa2";
          } else if (lowerVal === "update") {
            bg = "#e8f5e9";
            color = "#2e7d32";
          } else if (lowerVal === "not update") {
            bg = "#fff3e0";
            color = "#e65100";
          }

          return (
            <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
              <span className="badge" style={{ backgroundColor: bg, color: color, padding: "6px 10px", borderRadius: "12px", fontSize: "0.80rem" }}>
                {val}
              </span>
            </div>
          );
        },
      },
      {
        label: "Description",
        key: "Description",
        width: "200px",
        render: (r) => (
          <div className="text-truncate" style={{ cursor: "pointer", width: "100%" }} title={r.Description || r.HoldReason} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.Description || r.HoldReason || "-"}
          </div>
        ),
      },
      {
        label: "Support Team",
        key: "IsSupportTeam",
        width: "100px",
        render: (r) => (
          <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
            {r.IsSupportTeam ? (
              <span className="badge bg-info text-dark">Yes</span>
            ) : (
              <span className="badge bg-light text-secondary border">No</span>
            )}
          </div>
        ),
      },
      {
        label: "Status",
        key: "Status",
        width: "120px",
        render: (r) => {
          const status = r.Status ? r.Status.trim() : "";
          const bgColor = getStatusColor(status);
          const textColor = status === "On Hold" ? "#333" : "#fff";

          return (
            <div style={{ cursor: "pointer", width: "100%" }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
              <span style={{ backgroundColor: bgColor, color: textColor, padding: "6px 12px", borderRadius: "12px", fontSize: "0.8rem", fontWeight: "600", display: "inline-block", minWidth: "80px", textAlign: "center", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                {status ? displayStatus(status) : "-"}
              </span>
            </div>
          );
        },
      },
    ],
    [handleRowClick],
  );

  /* Memoised because it is the root of every derived list on this page.
     `x?.y?.z || []` builds a NEW empty array on every render, so each memo
     downstream saw a changed dependency and recomputed — the filtering, the
     option lists, the totals, all of it, on every keystroke in the search
     box. Only recompute when the fetched payload itself changes. */
  const rawData = useMemo(
    () => reportListData?.data?.data || [],
    [reportListData],
  );

  const userFilteredData = useMemo(() => {
    if (userFilter.includes("All") || userFilter.length === 0) return rawData;
    // Match on a NORMALIZED name (trim + lowercase) so a casing / extra-space
    // difference between the users master and the report row never silently
    // drops that employee's tasks (the "some tasks not shown" bug).
    const targets = userFilter.map((n) => String(n || "").trim().toLowerCase());
    return rawData.filter((item) =>
      targets.includes(String(item.UserName || "").trim().toLowerCase())
    );
  }, [rawData, userFilter]);

  /* What each attribute filter can offer, read off the rows that came back.
     Sorted, de-duplicated, blanks dropped — a blank is not a value somebody
     would choose, and "(none)" as an option would filter to rows the report
     shows as empty, which nobody is looking for. */
  const attrOptions = useMemo(() => {
    const out = {};
    ATTRIBUTE_FILTERS.forEach((f) => {
      const seen = new Set();
      rawData.forEach((r) => {
        const v = f.value ? f.value(r) : r[f.field];
        const text = String(v ?? "").trim();
        if (text) seen.add(text);
      });
      out[f.key] = [...seen].sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, [rawData]);

  /* Client, Team, Work and Menu, applied after the user filter and before the
     scope switch — so the Pending / All counts describe the set the report
     will actually show rather than the set before these narrowed it.

     Every filter left on "All" costs nothing: the loop skips it entirely. */
  const attrFilteredData = useMemo(() => {
    const active = ATTRIBUTE_FILTERS.filter((f) => {
      const sel = attrFilters[f.key] || [];
      return sel.length > 0 && !sel.includes("All");
    });
    if (!active.length) return userFilteredData;

    return userFilteredData.filter((row) =>
      active.every((f) => {
        const v = f.value ? f.value(row) : row[f.field];
        return attrFilters[f.key].includes(String(v ?? "").trim());
      }),
    );
  }, [userFilteredData, attrFilters]);

  /* How many filters are narrowing the report, for the badge on the funnel.
     Counts the columns, not the values: "3 filters" means three questions
     asked of the rows, which is what the reader needs to know. */
  const attrFilterCount = useMemo(
    () =>
      ATTRIBUTE_FILTERS.filter((f) => {
        const sel = attrFilters[f.key] || [];
        return sel.length > 0 && !sel.includes("All");
      }).length,
    [attrFilters],
  );

  /* The dialog's conditions are matched against the SAME accessor the printed
     column uses, so filtering Client on "KAS" finds the row whose cell reads
     KAS rather than whatever the underlying record happens to store. */
  const filterColumns = useMemo(
    () =>
      printColumns.map((c) => ({
        key: c.key,
        label: c.label,
        type: c.type,
        searchValue: c.value,
      })),
    [printColumns],
  );

  /* User dropdown first, then the dialog's conditions. Everything downstream
     — the document, the totals chips, both exports — reads this, so the
     report on screen and the file exported from it always agree. */
  /* The Pending / All switch, applied before the dialog's conditions so the
     counts on the switch itself describe the same set the report will show. */
  const scopedData = useMemo(
    () =>
      reportScope === "pending"
        ? attrFilteredData.filter((row) => isPendingStatus(row.Status))
        : attrFilteredData,
    [attrFilteredData, reportScope],
  );

  /* Shown on the Pending / All toggle, so the split is visible before you
     switch rather than only after. */
  const scopeCounts = useMemo(
    () => ({
      total: attrFilteredData.length,
      pending: attrFilteredData.filter((row) => isPendingStatus(row.Status)).length,
    }),
    [attrFilteredData],
  );

  const filteredData = useMemo(
    () =>
      filterList.length === 0
        ? scopedData
        : scopedData.filter((row) =>
            matchesAdvancedFilters(row, filterList, filterColumns),
          ),
    [scopedData, filterList, filterColumns],
  );

  /* Estimate and Different Time belong to the TASK. Worked belongs to one
     sitting at it.
     ---------------------------------------------------------------------
     A row here is one stretch of work — the procedure pairs each "In Progress"
     with the status change that ends it. Put a task on hold and pick it up
     again and you get a second row, a third, a sixth. Every one of those rows
     carried the task's FULL estimate, and Different Time was computed on the
     row, so a one-hour task worked in six sittings was measured against six
     separate hours:

         Cotton Correction checking   est 01:00   worked 01:34   diff -00:34
         Cotton Correction checking   est 01:00   worked 00:04   diff +00:56
         Cotton Correction checking   est 01:00   worked 00:14   diff +00:46
         Cotton Correction checking   est 01:00   worked 00:00   diff +01:00
         Cotton Correction checking   est 01:00   worked 01:15   diff -00:15
         Cotton Correction checking   est 01:00   worked 00:44   diff +00:16
                                                          total  +02:09 saved

     One hour estimated, three hours fifty-one actually spent — reported as
     over two hours SAVED. Every pause the person took made the number better.
     The column totals were wrong for the same reason: six rows of a
     sixty-minute estimate added up to six hours of estimate.

     So the estimate and the difference are attributed ONCE per task, on the
     row where its last stretch of work in this report ends, and left blank on
     the rest. Worked stays per row, because per row is what it measures. The
     three columns then agree at the bottom of the page: total Different Time
     is exactly total Est. Time minus total Worked, which it never was before.

     Grouped by QueryCode, falling back to person + task name for any row that
     somehow arrives without one. Totals are taken over the rows the report is
     SHOWING — narrow the date range to half a task and you are told about the
     half you asked for, which is the only answer this page can honestly give.
     A whole-task estimate against part of a task is the one soft spot left,
     and it is inherent in windowing the report at all. */
  /* The management report's three tables, fetched only when that report is
     the one on screen. Each has its own source and its own idea of a date:
     attendance spans the window, the query status is a snapshot as of its
     end, and the complaint list is the same query rows the Over All Report
     reads. */
  const [mgmt, setMgmt] = useState({
    loading: false,
    attendance: [],
    complaints: [],
    queryStatus: [],
  });

  const isSectioned = activeType.sectioned === true;

  useEffect(() => {
    if (!isSectioned || view !== "report") return undefined;
    if (!dateRange.startDate || !dateRange.endDate) return undefined;

    let alive = true;
    setMgmt((m) => ({ ...m, loading: true }));

    const from = format(dateRange.startDate, "yyyy-MM-dd");
    const to = format(dateRange.endDate, "yyyy-MM-dd");

    /* Three endpoints, three nesting depths.
     *
     * The two new ones hand back successHandler(recordset), so the array is
     * at res.data.data. /report/list wraps its rows in a paging envelope
     * first — { totalRecords, totalPages, data } — so its array is one level
     * further down, and an unwrap that stopped at the first .data found an
     * object, decided it was not a list, and returned nothing. That is why
     * Client Support & Complaints printed "Nothing to report".
     *
     * Walk down until an array turns up rather than guessing a depth. */
    const rowsOf = (res) => {
      let node = res;
      for (let i = 0; i < 5 && node; i += 1) {
        if (Array.isArray(node)) return node;
        node = node.data;
      }
      return [];
    };

    Promise.all([
      dispatch(staffAttendanceApis({ fromDate: from, toDate: to })).unwrap(),
      dispatch(
        clientQueryStatusApis({ asOfDate: to, agingDays: 2 }),
      ).unwrap(),
      /* FromDate / ToDate, capitalised: that endpoint reads req.query.FromDate
         and the lower-case pair arrived as undefined, so the procedure was
         handed two NULLs and the section came back empty. */
      dispatch(reportListsApis({ FromDate: from, ToDate: to })).unwrap(),
    ])
      .then(([att, qs, det]) => {
        if (!alive) return;
        setMgmt({
          loading: false,
          attendance: rowsOf(att),
          queryStatus: rowsOf(qs),
          /* Every query in the window, whatever its status.
           *
           * This was filtered to open work on the reasoning that a management
           * sheet listing finished tasks is one nobody reads to the end. The
           * sample says otherwise: its Status column carries Complete and Not
           * Possible alongside the rest, because the table is the account of
           * the day's work and what got FINISHED is half of that account. */
          complaints: rowsOf(det),
        });
      })
      .catch(() => {
        if (!alive) return;
        setMgmt({ loading: false, attendance: [], complaints: [], queryStatus: [] });
      });

    return () => {
      alive = false;
    };
  }, [isSectioned, view, dateRange.startDate, dateRange.endDate, dispatch]);

  /* The line under Client Query Status.
   *
   * The procedure returns a row per client and stops, so twenty-six rows of
   * counts ended with nothing to add them up — the one number a management
   * sheet is read for. Computed here rather than asked of the procedure
   * because it is arithmetic over rows already in hand.
   *
   * The counts add. The two age columns do not:
   *
   *   Oldest  is the worst of the worst, so it is a MAX. Adding the oldest
   *           item of each client would produce a number of days nothing has
   *           actually waited.
   *
   *   Avg Age is weighted by Pending, not averaged across clients. A client
   *           with one 90-day item and one with forty 2-day items do not
   *           deserve equal say in "how old is the backlog"; the mean of the
   *           two means would claim 46 days when almost nothing is that old.
   */
  const withQueryStatusTotal = (input) => {
    if (!Array.isArray(input) || input.length === 0) return input || [];

    /* A client with nothing in any column is not a line worth printing.
     *
     * They appeared once Completed became TODAY's rather than all time: a
     * client whose only activity was finished months ago now scores zero
     * everywhere, and a management sheet is read for what needs attention,
     * not for a roll call of everyone who has ever raised a query.
     *
     * Every numeric column is tested, ageing included — a client with no
     * counts but a 40-day oldest item still has something outstanding and
     * stays. */
    const COUNTS = [
      "Opening",
      "TodayRaised",
      "NotPossible",
      "Completed",
      "PendingClient",
      "PendingOwn",
      "Pending",
      "OnHold",
      "NotStarted",
      "InProgress",
      "Testing",
      "AgingOver",
      "OldestDays",
      "AvgAgeDays",
    ];

    const rows = input.filter((r) =>
      COUNTS.some((k) => (Number(r[k]) || 0) !== 0),
    );

    if (rows.length === 0) return [];

    const sum = (k) => rows.reduce((n, r) => n + (Number(r[k]) || 0), 0);
    const pending = sum("Pending");

    const weightedAge = pending
      ? Math.round(
          rows.reduce(
            (n, r) => n + (Number(r.AvgAgeDays) || 0) * (Number(r.Pending) || 0),
            0,
          ) / pending,
        )
      : 0;

    return [
      ...rows,
      {
        __total: true,
        Client: `TOTAL (${rows.length})`,
        Opening: sum("Opening"),
        TodayRaised: sum("TodayRaised"),
        NotPossible: sum("NotPossible"),
        Completed: sum("Completed"),
        PendingClient: sum("PendingClient"),
        PendingOwn: sum("PendingOwn"),
        Pending: pending,
        OnHold: sum("OnHold"),
        NotStarted: sum("NotStarted"),
        InProgress: sum("InProgress"),
        Testing: sum("Testing"),
        AgingOver: sum("AgingOver"),
        OldestDays: rows.reduce(
          (n, r) => Math.max(n, Number(r.OldestDays) || 0),
          0,
        ),
        AvgAgeDays: weightedAge,
      },
    ];
  };

  /* Handed to the viewer as one document of three tables. The grand-total row
     the procedure emits is marked so the sheet can weight it, rather than
     letting it read as one more client. */
  const managementSections = useMemo(
    () => [
      {
        key: "attendance",
        title: "Staff Attendance & Utilization",
        columns: ATTENDANCE_COLUMNS,
        rows: mgmt.attendance,
      },
      {
        key: "complaints",
        title: "Client Support & Complaints",
        columns: COMPLAINT_COLUMNS,
        rows: mgmt.complaints,
      },
      {
        key: "queryStatus",
        title: "Client Query Status",
        columns: QUERY_STATUS_COLUMNS,
        rows: withQueryStatusTotal(mgmt.queryStatus),
        isTotalRow: (r) => r.__total === true,
      },
    ],
    [mgmt],
  );

  /* One line per TASK, not per stretch of work on it.
   *
   * A task that is started, put On Hold and picked up again produces a
   * tracker row each time, and the report printed every one of them: five
   * lines reading "Yarn Web Conversion / KAS / In Progress", same client,
   * same menu, differing only in a pair of timestamps. Read as a list of work
   * done it is simply wrong — the task was done once, over five sittings.
   *
   * So the sittings are merged: the earliest start, the latest end, and the
   * worked and idle minutes added up. `Sessions` records how many there were,
   * which is the one fact the merge would otherwise throw away and the reason
   * a single line can span seven hours and hold two of them.
   *
   * Merged per PERSON as well as per task. Two developers on one task are two
   * lines, because "who did this" is what a user-wise report is for.
   *
   * Status comes from the last sitting: a task held twice and then finished
   * is Complete, and the report should say so rather than quoting whichever
   * state it happened to be in first. */
  const consolidate = (rows) => {
    const byTask = new Map();

    rows.forEach((r) => {
      const key = `${r.QueryCode ?? r.NatureofQuery ?? ""}|${r.UserName ?? ""}`;
      const start = r.StartTime ? new Date(r.StartTime).getTime() : null;
      const end = r.EndTime ? new Date(r.EndTime).getTime() : null;

      const prev = byTask.get(key);
      if (!prev) {
        byTask.set(key, {
          ...r,
          Sessions: 1,
          RunningMinutes: Number(r.RunningMinutes) || 0,
          IdleMinutes: Number(r.IdleMinutes) || 0,
          _start: start,
          _end: end,
        });
        return;
      }

      prev.Sessions += 1;
      prev.RunningMinutes += Number(r.RunningMinutes) || 0;
      prev.IdleMinutes += Number(r.IdleMinutes) || 0;

      if (start !== null && (prev._start === null || start < prev._start)) {
        prev._start = start;
        prev.StartTime = r.StartTime;
      }
      if (end !== null && (prev._end === null || end >= prev._end)) {
        prev._end = end;
        prev.EndTime = r.EndTime;
        /* The state it ended in, and the note written with it. */
        prev.Status = r.Status;
        prev.ChangeStatus = r.ChangeStatus;
        if (r.Description) prev.Description = r.Description;
      }
    });

    return [...byTask.values()].map(({ _start, _end, ...row }) => row);
  };

  const rowsWithTaskTotals = useMemo(() => {
    /* Merge first, then measure. With one line per task the estimate belongs
       on it outright — the old code carried the total on the LAST of a task's
       rows and blanked the others, which is why four of those five lines
       showed "-" under Est. Time and the fifth showed 02:30. Nothing to blank
       now. */
    const merged = consolidate(filteredData || []);

    /* One row per task now, so every row carries its own totals. The old
       version had to pick a row to hang them on and blank the rest, because
       printing the estimate against five sittings would have promised the
       same hours five times. */
    return merged.map((r) => {
      const estimate = Number(r?.EstimateTime) || 0;
      const worked = Number(r?.RunningMinutes) || 0;
      return {
        ...r,
        TaskEstimate: estimate,
        TaskWorked: worked,
        TaskDiff: estimate - worked,
        TaskSessions: r.Sessions || 1,
      };
    });
  }, [filteredData]);

  /* Conditions actually narrowing something. A column picked with no value
     typed yet matches everything, so counting it would put a badge on the
     funnel over a report nothing had been done to. */
  const activeFilterCount = useMemo(
    () =>
      filterList.filter((f) =>
        f.startDate || f.endDate ? true : String(f.value || "").trim() !== "",
      ).length,
    [filterList],
  );

  // Working / Idle / Records totals are summed from the rows on screen (the
  // employee + any in-table column filter), so the chips always match the table.
  const summaryTotals = useMemo(() => {
    const rows = finalExportData !== null ? finalExportData : filteredData;
    let working = 0;
    let idle = 0;
    (rows || []).forEach((r) => {
      working += Number(r?.RunningMinutes) || 0;
      idle += Number(r?.IdleMinutes) || 0;
    });
    return {
      count: (rows || []).length,
      workingHHMM: minutesToHHMM(working),
      idleHHMM: minutesToHHMM(idle),
    };
  }, [finalExportData, filteredData]);

  const totalWorkingHHMM = summaryTotals.workingHHMM;
  const totalIdleHHMM = summaryTotals.idleHHMM;

  /* The data grid that used to be here is gone — this page is the report
     document now, the same as the Overall Report. Its callbacks fed the
     export, and all three already had fallbacks, which is why removing it
     does not break Export:

       rows    -> filteredData        (the date range + user filter)
       columns -> the full column set
       filters -> none listed on the PDF

     `columns` is still needed for that fallback, so it stays. */

  /* The daily management workbook. Built from the Overall Report's own
     endpoints rather than this page's rows: sp_Sky_DashBoard_User_WorkingHistory
     returns UserName / ShortName / RunningMinutes where the workbook wants
     AssigneeName / ClientName / TimeTaken, so feeding it this grid's data would
     produce a sheet of blanks. Same file as the Overall Report produces — one
     helper, so the two cannot drift. */
  /* Everything narrowing the report, on one badge: the four column pickers
     plus whatever the advanced dialog holds. Two separate counts on the same
     funnel would be two numbers for one question. */
  const totalFilterCount = attrFilterCount + activeFilterCount;

  /* The download this page used to offer is gone — Support Team Report is a
     report type now, read on screen and exported from the viewer's own menu
     like every other one. downloadSupportTeamReport still exists in
     utils/supportTeamReport and still builds the full four-table workbook, so
     putting the button back is a handler and a card, not a rebuild. */

  const handleExportClick = async () => {
    const dataToExport = finalExportData !== null ? finalExportData : rowsWithTaskTotals;
    const columnsToExport = visibleColumns && visibleColumns.length > 0 ? visibleColumns : columns;

    if (dataToExport.length === 0) {
      alert("No data available to export.");
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    for (let i = 0; i <= 90; i += 10) {
      setDownloadProgress(i);
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    const liveWidths = {};
    const tableEl = document.querySelector(".report-container table:not(.rv-table)");
    if (tableEl) {
      const thElements = tableEl.querySelectorAll("th");
      if (thElements.length >= columnsToExport.length) {
        columnsToExport.forEach((col, index) => {
          if (thElements[index]) {
            liveWidths[col.key] = thElements[index].getBoundingClientRect().width;
          }
        });
      }
    }

    let totalEst = 0;
    let totalTaken = 0;
    let totalIdle = 0;

    dataToExport.forEach((row) => {
      if (row.EstimateTime) totalEst += Number(row.EstimateTime) || 0;
      if (row.RunningMinutes) {
        totalTaken += Number(row.RunningMinutes) || 0;
      } else {
        const durationObj = calculateDuration(row.StartTime, row.EndTime);
        if (durationObj && durationObj.totalMinutes)
          totalTaken += durationObj.totalMinutes;
      }
      if (row.IdleMinutes) totalIdle += Number(row.IdleMinutes) || 0;
    });

    const footerMap = {
      UserName: "TOTAL",
      EstimateTime: minutesToHHMM(totalEst),
      RunningMinutes: minutesToHHMM(totalTaken),
      IdleMinutes: minutesToHHMM(totalIdle),
    };

    const activeFiltersList = [];

    if (!userFilter.includes("All")) {
      const usersStr = userFilter.join(", ");
      activeFiltersList.push(`Users: ${usersStr}`);
    }

    tableFilters.forEach((f) => {
      if (f.value || (f.startDate && f.endDate)) {
        const col = columns.find((c) => c.key === f.column);
        const label = col ? col.label : f.column;

        if (f.operator === "between") {
          activeFiltersList.push(`${label}: ${f.startDate} to ${f.endDate}`);
        } else {
          activeFiltersList.push(`${label}: ${f.value}`);
        }
      }
    });

    try {
      const formattedDataToExport = dataToExport.map((row) => ({
        ...row,
        EstimateTime: minutesToHHMM(row.EstimateTime),
        RunningMinutes: minutesToHHMM(row.RunningMinutes),
        IdleMinutes: minutesToHHMM(row.IdleMinutes),
        StartTime:
          formatDateTimeDisplay(row.StartTime),
        EndTime:
          formatDateTimeDisplay(row.EndTime),
      }));

      exportToPDF(
        formattedDataToExport,
        columnsToExport,
        "User Query Report",
        dateRange,
        footerMap,
        liveWidths,
        activeFiltersList,
        // The Status / Is Update / Support columns get the same value-based
        // colours the Detailed Query Report uses. See REPORT_CELL_COLORS.
        REPORT_CELL_COLORS,
      );

      setDownloadProgress(100);
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
      }, 800);
    } catch (error) {
      console.error("Export Failed", error);
      setIsDownloading(false);
      alert("Export failed");
    }
  };

  return (
    <div className="report-container p-2 p-md-4">
      {/* <div className="bg-white rounded-4 shadow-sm p-3 mb-4 border">
        
        <div className="d-flex d-lg-none flex-column gap-3">
          <h4 className="report-title">User Query Report</h4>
          
          <div className="d-flex align-items-center gap-2 mb-2">
             <span className="badge bg-primary-subtle text-primary border border-primary-subtle flex-grow-1 py-2">
               <i className="bi bi-briefcase-fill me-1"></i> Work: {totalWorkingHHMM}
             </span>
             <span className="badge bg-warning-subtle text-warning border border-warning-subtle flex-grow-1 py-2">
               <i className="bi bi-cup-hot-fill me-1"></i> Idle: {totalIdleHHMM}
             </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1">
              <UserFilterDropdown
                usersList={usersList}
                selectedUsers={userFilter}
                onChange={setUserFilter}
                className="w-100"
              />
            </div>
            <div className="d-flex align-items-center justify-content-center bg-light px-3 rounded-3 border" style={{ height: "40px" }}>
              <span className="badge bg-primary text-white rounded-pill">
                {finalExportData !== null ? finalExportData.length : filteredData.length}
              </span>
            </div>
          </div>
          <div className="w-100">
            <div className="w-100" style={{ minHeight: "40px" }}>
              <DateRangeFilter startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={handleDateChange} />
            </div>
          </div>
          <button className="btn btn-light border shadow-sm w-100 d-flex align-items-center justify-content-center gap-2" onClick={() => window.location.reload()} style={{ height: "40px" }}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          <button className="btn btn-primary shadow-sm w-100 fw-bold d-flex align-items-center justify-content-center gap-2" onClick={handleExportClick} style={{ height: "40px" }}>
            <i className="bi bi-file-earmark-arrow-down-fill"></i> Export PDF
          </button>
        </div>

        <div className="d-none d-lg-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <h4 className="report-title mb-0">User Query Report</h4>
            
            <div className="d-flex align-items-center bg-light px-3 py-1 rounded-pill border gap-3">
              <div className="d-flex align-items-center">
                <span className="text-muted small fw-bold me-2" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>RECORDS</span>
                <span className="badge bg-primary text-white rounded-pill shadow-sm">
                  {finalExportData !== null ? finalExportData.length : filteredData.length}
                </span>
              </div>
              
              <div className="vr border-secondary opacity-25" style={{ height: "20px" }}></div>
              
              <div className="d-flex align-items-center" title="Total Working Time">
                 <i className="bi bi-briefcase-fill text-primary me-2 opacity-75"></i>
                 <span className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>{totalWorkingHHMM}</span>
              </div>
              
              <div className="vr border-secondary opacity-25" style={{ height: "20px" }}></div>
              
              <div className="d-flex align-items-center" title="Total Idle Time">
                 <i className="bi bi-cup-hot-fill text-warning me-2 opacity-75"></i>
                 <span className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>{totalIdleHHMM}</span>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <UserFilterDropdown usersList={usersList} selectedUsers={userFilter} onChange={setUserFilter} />
            <DateRangeFilter startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={handleDateChange} />
            <div className="vr mx-2 text-muted h-50"></div>
            <button className="btn btn-light border shadow-sm" onClick={() => window.location.reload()} title="Refresh">
              <i className="bi bi-arrow-clockwise"></i>
            </button>
            <button className="btn btn-primary shadow-sm fw-bold d-flex align-items-center gap-2" onClick={handleExportClick}>
              <i className="bi bi-box-arrow-up"></i> Export
            </button>
          </div>
        </div>
      </div> */}

      {/* --- DESKTOP VIEW --- */}
      {/* The header keeps what it can always answer — which report this is,
          and how much is in it. Everything that DECIDES the report moved to
          the setup screen below, where each control gets a label and a size.

          Hidden while the document is open, the way the SWAS report screens
          do it: at that point the context bar names the report and the page
          wants its height back. */}
      {view === "setup" && (
        <PageHeader
          title={
            <div className="d-flex align-items-center text-nowrap">
              <i className="bi bi-person-lines-fill me-2 text-primary"></i>
              <span>User Query Report</span>
            </div>
          }
          actions={
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <StatChip
                icon="bi-clipboard-data-fill"
                tone="blue"
                label="Records"
                value={
                  finalExportData !== null
                    ? finalExportData.length
                    : filteredData.length
                }
              />
              <StatChip
                icon="bi-briefcase-fill"
                tone="green"
                label="Working Time"
                value={totalWorkingHHMM}
              />
              <StatChip
                icon="bi-cup-hot-fill"
                tone="orange"
                label="Idle Time"
                value={totalIdleHHMM}
              />
              <button
                className="btn btn-light border shadow-sm"
                onClick={() => window.location.reload()}
                title="Refresh"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
          }
        />
      )}

      {/* The report itself is the page — no grid above it. Always grouped by
          person: that is the whole point of this report, and a per-person
          Worked / Idle sub-total is what it is read for. */}

      {/* Suggestions in the Value box come from the user-filtered rows, so it
          offers the values that are actually on this report. */}
      {showFilterPanel && (
        <FilterPanel
          filterList={filterList}
          setFilterList={setFilterList}
          onClose={() => setShowFilterPanel(false)}
          onClearAll={() => setFilterList([])}
          columns={filterColumns}
          data={userFilteredData}
        />
      )}


      {/* ---------------------------------------------------------------
          SETUP — what the report is going to be.
          --------------------------------------------------------------- */}
      {view === "setup" && (
        <div className="rsx">
          <section className="rsx__card">
            <div className="rsx__head">
              <span className="rsx__legend">Date Range</span>
            </div>

            <div className="rsx__dates">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`rsx__preset${
                    activePreset === preset.key ? " is-active" : ""
                  }`}
                  onClick={() => applyPreset(preset)}
                  /* Pending Only reads all time on purpose, so the buttons
                     have nothing to set while it is on. */
                  disabled={reportScope === "pending"}
                >
                  <i className={`bi ${preset.icon}`} />
                  {preset.label}
                </button>
              ))}

              <span className="rsx__dategap" />

              <input
                type="date"
                className="rsx__date"
                value={dateInputValue(dateRange.startDate)}
                onChange={setDateEnd("start")}
                disabled={reportScope === "pending"}
                aria-label="From date"
              />
              <span className="rsx__to">to</span>
              <input
                type="date"
                className="rsx__date"
                value={dateInputValue(dateRange.endDate)}
                onChange={setDateEnd("end")}
                disabled={reportScope === "pending"}
                aria-label="To date"
              />
            </div>
          </section>

          <div className="rsx__cols">
            <section className="rsx__card">
              <div className="rsx__head">
                <span className="rsx__legend">Filters</span>
                <button
                  type="button"
                  className="rsx__hide"
                  onClick={() => setShowFilters((v) => !v)}
                >
                  {showFilters ? "Hide" : "Show"}
                  <i className={`bi bi-chevron-${showFilters ? "up" : "down"}`} />
                </button>
              </div>

              {showFilters && (
                <div className="rsx__body">
                  <div className="rsx__field">
                    <span className="rsx__label">User</span>
                    <UserFilterDropdown
                      usersList={usersList}
                      selectedUsers={userFilter}
                      onChange={setUserFilter}
                      className="w-100"
                    />
                  </div>

                  {/* One row per column worth narrowing on. They sit under
                      User because that is the order the report reads in — who
                      did it, then for whom, then where. */}
                  {ATTRIBUTE_FILTERS.map((f) => (
                    <div className="rsx__field" key={f.key}>
                      <span className="rsx__label">{f.label}</span>
                      <ValueFilterDropdown
                        icon={f.icon}
                        label={f.label}
                        plural={f.plural}
                        options={attrOptions[f.key] || []}
                        selected={attrFilters[f.key] || ["All"]}
                        onChange={(v) => setAttrFilter(f.key, v)}
                      />
                    </div>
                  ))}

                  <div className="rsx__field">
                    <span className="rsx__label">Advanced</span>
                    <button
                      type="button"
                      className={`rsx__control rsx__filterbtn${
                        activeFilterCount ? " is-on" : ""
                      }`}
                      onClick={() => setShowFilterPanel(true)}
                    >
                      <i
                        className={`bi ${
                          activeFilterCount ? "bi-funnel-fill" : "bi-funnel"
                        }`}
                      />
                      {activeFilterCount
                        ? `${activeFilterCount} filter${
                            activeFilterCount === 1 ? "" : "s"
                          } applied`
                        : "Add column filters…"}
                      {activeFilterCount > 0 && (
                        <span className="rsx__count">{activeFilterCount}</span>
                      )}
                    </button>
                  </div>

                  <p className="rsx__note">
                    Filters narrow the rows the report is built from — the
                    document is generated from whatever is left.
                  </p>
                </div>
              )}
            </section>

            {/* The scope bar and the type cards are one grid item, not two.
                .rsx__cols is a two-column grid, so a loose sibling here takes
                the right column and shunts the cards it belongs to onto the
                next row, underneath the filters. */}
            <div className="rsx__col">
            {/* Which rows the report is built from — All work, or only what
                is still outstanding.
                *
                * Promoted out of the filter list and given its own bar because
                * it is not the same kind of choice as Client or Priority. Those
                * narrow a report; this one decides which report it is, and
                * every type below obeys it. Buried in a select among nine
                * others it read as one more optional filter. */}
            <div className="rsx__scope" role="group" aria-label="Report scope">
              <span className="rsx__scope-label">Show</span>

              <div className="rsx__scope-set">
                {SCOPES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`rsx__scope-btn${
                      reportScope === s.key ? " is-active" : ""
                    }`}
                    onClick={() => setReportScope(s.key)}
                    aria-pressed={reportScope === s.key}
                  >
                    <i className={`bi ${s.icon}`} aria-hidden="true" />
                    {s.label}
                    <span className="rsx__scope-count">
                      {s.key === "pending"
                        ? scopeCounts.pending
                        : scopeCounts.total}
                    </span>
                  </button>
                ))}
              </div>

              {/* Pending deliberately ignores the dates above — a backlog cut
                  to this week is not a backlog. Said here rather than left for
                  somebody to notice from the row count. */}
              {reportScope === "pending" && (
                <span className="rsx__scope-note">
                  <i className="bi bi-info-circle" aria-hidden="true" />
                  All time — the date range above does not apply
                </span>
              )}
            </div>

            <section className="rsx__card">
              <div className="rsx__head">
                <span className="rsx__legend">Report Type</span>
                <button
                  type="button"
                  className="rsx__hide"
                  onClick={() => setShowTypes((v) => !v)}
                >
                  {showTypes ? "Hide" : "Show"}
                  <i className={`bi bi-chevron-${showTypes ? "up" : "down"}`} />
                </button>
              </div>

              {showTypes && (
                <div className="rsx__body">
                  <div className="rsx__types">
                    {REPORT_TYPES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        className={`rsx__type${
                          reportType === t.key ? " is-active" : ""
                        }`}
                        /* Choosing a type opens the report. The Generate
                           button it replaces asked the reader to say the same
                           thing twice — pick the report, then confirm they
                           meant it — and since every setting on this screen
                           has a default, the button was never guarding
                           against an incomplete choice. Back returns here
                           with everything still selected. */
                        onClick={() => {
                          setReportType(t.key);
                          setView("report");
                        }}
                        aria-pressed={reportType === t.key}
                      >
                        <span
                          className={`rsx__type-icon rsx__type-icon--${
                            t.tone || "blue"
                          }`}
                        >
                          <i className={`bi ${t.icon}`} />
                        </span>
                        <span className="rsx__type-label">{t.label}</span>
                      </button>
                    ))}

                  </div>
                </div>
              )}
            </section>
            </div>
          </div>

          {/* With the button gone, this line is what tells you the screen is
              waiting on you — and how much the report will hold when you
              pick. Without it a filtered-to-nothing setup looks identical to
              a ready one right up until the empty document opens. */}
          <div className="rsx__go">
            <span className="rsx__ready">
              {status === "loading" ? (
                "Loading rows…"
              ) : filteredData.length === 0 ? (
                <>
                  <i className="bi bi-exclamation-circle me-1" />
                  Nothing matches these filters — nothing to report on yet.
                </>
              ) : (
                <>
                  <i className="bi bi-hand-index-thumb me-1" />
                  Choose a report type above to open it ·{" "}
                  <strong>{filteredData.length}</strong> rows ready
                </>
              )}
            </span>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------
          REPORT — the document, with the setup one Back button away.

          Kept mounted and hidden rather than unmounted: the viewer holds the
          page you were on, the zoom and the columns you switched off, and
          stepping back to change a date would otherwise throw all three away.
          --------------------------------------------------------------- */}
      <div
        className="position-relative"
        style={{
          minHeight: "300px",
          display: view === "report" ? undefined : "none",
        }}
      >
        {status === "loading" && (
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 99, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "16px" }}>
            <Loader isLoading={true} />
          </div>
        )}

        {status === "failed" && (
          <div className="alert alert-danger m-0 mb-3">
            Error loading reports.
          </div>
        )}

        <ReportViewer
          show
          embedded
          visible={view === "report"}
          splitChrome
          /* Back rather than a close cross: it goes somewhere — the screen
             the report was asked for on — rather than dismissing something. */
          onClose={() => setView("setup")}
          backLabel="Back"
          /* The three things the setup screen decided, restated where the
             document can be read against them, and each one a way back to
             the panel that set it. */
          contextLeft={
            /* Each chip changes its own setting where it stands, and the
               document redraws underneath. Back is what returns to the setup
               screen; nothing on this bar leaves the report. */
            <div className="rv-chips" ref={chipsRef}>
              {/* The date reads as a card rather than a chip: it is the one
                  control here carrying two lines — what you picked, and the
                  dates that turned out to mean. */}
              <div className="rv-chipwrap">
                <button
                  type="button"
                  className={`rv-datechip${chipMenu === "date" ? " is-open" : ""}`}
                  onClick={() =>
                    setChipMenu((m) => (m === "date" ? null : "date"))
                  }
                  title="Change the date range"
                >
                  <i className="bi bi-calendar3" />
                  <span className="rv-datechip__text">
                    <strong>{presetLabel}</strong>
                    <em>{rangeLabel}</em>
                  </span>
                </button>

                {chipMenu === "date" && (
                  <div className="rv-datemenu">
                    <div className="rv-datemenu__body">
                      {/* Presets down the side, calendar beside them: the
                          named period is what nine readings in ten want, and
                          the calendar is there for the tenth. */}
                      <div className="rv-datemenu__presets">
                        {DATE_PRESETS.map((preset) => (
                          <button
                            key={preset.key}
                            type="button"
                            className={`rv-datemenu__preset${
                              activePreset === preset.key ? " is-active" : ""
                            }`}
                            disabled={reportScope === "pending"}
                            onClick={() => applyPreset(preset)}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <div className="rv-datemenu__cal">
                        <DatePicker
                          selected={dateRange.startDate}
                          startDate={dateRange.startDate}
                          endDate={dateRange.endDate}
                          onChange={handleCalendarRange}
                          selectsRange
                          inline
                          monthsShown={1}
                          calendarStartDay={1}
                          disabled={reportScope === "pending"}
                        />

                        <div className="rv-datemenu__inputs">
                          <input
                            type="date"
                            value={dateInputValue(dateRange.startDate)}
                            onChange={setDateEnd("start")}
                            disabled={reportScope === "pending"}
                            aria-label="From date"
                          />
                          <span>&rarr;</span>
                          <input
                            type="date"
                            value={dateInputValue(dateRange.endDate)}
                            onChange={setDateEnd("end")}
                            disabled={reportScope === "pending"}
                            aria-label="To date"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rv-datemenu__foot">
                      <button
                        type="button"
                        className="rv-datemenu__clear"
                        onClick={clearDates}
                        title="Back to the default seven-day window"
                      >
                        Clear
                      </button>
                      {reportScope === "pending" ? (
                        <span className="rv-datemenu__hint is-warn">
                          Pending only reads all time — dates stand down
                        </span>
                      ) : (
                        <span className="rv-datemenu__hint">
                          <i className="bi bi-check-circle" />
                          Click outside to apply
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Report type */}
              <div className="rv-chipwrap">
                <button
                  type="button"
                  className={`rv-chip${chipMenu === "type" ? " is-open" : ""}`}
                  onClick={() =>
                    setChipMenu((m) => (m === "type" ? null : "type"))
                  }
                  title="Change the report type"
                >
                  <span className="rv-chip__tile">
                    <i className={`bi ${activeType.icon}`} />
                  </span>
                  {activeType.label}
                  <i className="bi bi-caret-down-fill" style={{ fontSize: "0.6rem" }} />
                </button>

                {chipMenu === "type" && (
                  <div className="rv-typemenu">
                    {REPORT_TYPES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        className={`rv-typemenu__item${
                          reportType === t.key ? " is-active" : ""
                        }`}
                        onClick={() => {
                          setReportType(t.key);
                          setChipMenu(null);
                        }}
                      >
                        <span className={`rv-typemenu__tile rv-typemenu__tile--${t.tone || "blue"}`}>
                          <i className={`bi ${t.icon}`} />
                        </span>
                        <span className="rv-typemenu__label">{t.label}</span>
                        {reportType === t.key && (
                          <i className="bi bi-check2 rv-typemenu__tick" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Filters. Its own button rather than a chip that appears only
                  once something is filtering — a control you cannot find
                  until you have already used it is a control nobody finds. */}
              <div className="rv-chipwrap">
                <button
                  type="button"
                  className={`rv-iconchip${chipMenu === "filter" ? " is-open" : ""}${
                    totalFilterCount ? " is-on" : ""
                  }`}
                  onClick={() =>
                    setChipMenu((m) => (m === "filter" ? null : "filter"))
                  }
                  title="Filters"
                >
                  <i className="bi bi-funnel-fill" />
                  {totalFilterCount > 0 && (
                    <span className="rv-iconchip__count">{totalFilterCount}</span>
                  )}
                </button>

                {chipMenu === "filter" && (
                  <div className="rv-filtermenu">
                    <div className="rv-filtermenu__grid">
                      <span className="rv-filtermenu__label">User</span>
                      <UserFilterDropdown
                        usersList={usersList}
                        selectedUsers={userFilter}
                        onChange={setUserFilter}
                        className="w-100"
                      />

                      {ATTRIBUTE_FILTERS.map((f) => (
                        <React.Fragment key={f.key}>
                          <span className="rv-filtermenu__label">{f.label}</span>
                          <ValueFilterDropdown
                            icon={f.icon}
                            label={f.label}
                            plural={f.plural}
                            options={attrOptions[f.key] || []}
                            selected={attrFilters[f.key] || ["All"]}
                            onChange={(v) => setAttrFilter(f.key, v)}
                          />
                        </React.Fragment>
                      ))}

                      <span className="rv-filtermenu__label">Scope</span>
                      <select
                        className="rv-filtermenu__select"
                        value={reportScope}
                        onChange={(e) => setReportScope(e.target.value)}
                      >
                        <option value="all">All work ({scopeCounts.total})</option>
                        <option value="pending">
                          Pending only ({scopeCounts.pending})
                        </option>
                      </select>

                      <span className="rv-filtermenu__label">Advanced</span>
                      <button
                        type="button"
                        className={`rv-filtermenu__select rv-filtermenu__adv${
                          activeFilterCount ? " is-on" : ""
                        }`}
                        onClick={() => {
                          setChipMenu(null);
                          setShowFilterPanel(true);
                        }}
                      >
                        <i
                          className={`bi ${
                            activeFilterCount ? "bi-funnel-fill" : "bi-funnel"
                          }`}
                        />
                        {activeFilterCount
                          ? `${activeFilterCount} column filter${
                              activeFilterCount === 1 ? "" : "s"
                            }`
                          : "Add column filters…"}
                      </button>
                    </div>

                    <p className="rv-filtermenu__note">
                      <i className="bi bi-check-circle" />
                      Changes apply to the report as you make them.
                    </p>
                  </div>
                )}
              </div>
            </div>
          }
          title="User Query Report"
          /* The totals table at the back of the report. Counted by whatever
             the report is grouped by, or by person when it is not grouped —
             this is the User Query Report, so per person is the breakdown it
             exists to give. */
          appendSummary={!isSectioned}
          /* Two queues, one report. A task raised through the Common Team
             screen carries IsSupportTeam; everything else is the tech team's
             own work. The summary counted them together, which is the one
             split the two queues exist to make. */
          summarySegments={[
            {
              key: "common",
              label: "Common Task",
              test: (r) => Boolean(r.IsSupportTeam),
            },
            {
              key: "tech",
              label: "Tech Team",
              test: (r) => !r.IsSupportTeam,
            },
          ]}
          summaryBy={{
            key: "UserName",
            label: "User",
            value: (r) => r.UserName || "(none)",
          }}
          subtitle={activeType.key === "none" ? null : activeType.label}
          sections={isSectioned ? managementSections : null}
          dateRange={dateRange}
          columns={activeType.columns === "tl" ? tlColumns : printColumns}
          rows={rowsWithTaskTotals}
          groupBy={
            /* TL Report groups by person, so each lead reads a block per
               member with that member's own totals under it.
             *
             * It used to be one flat sheet with a single total at the foot,
             * on the reasoning that headers would break the shape the leads
             * already circulate. That was the wrong trade: a lead's question
             * is "how much did each of my people do", and a single figure at
             * the bottom of 357 rows answers it only by making somebody add
             * the rows up by hand. The grand total is still there underneath
             * the per-person ones, so nothing that used to be readable has
             * been taken away.
             *
             * The Assigned Staff column stays in the column set on purpose:
             * a group header scrolls off on a long block, and a row that
             * cannot say whose it is on its own is a row that has to be
             * scrolled back to identify. */
            activeType.columns === "tl"
              ? {
                  key: "UserName",
                  label: "User",
                  value: (r) => r.UserName || "(none)",
                }
              : activeType.key === "none"
              ? null
              : {
                  key: activeType.key,
                  label: activeType.label.replace(/ Wise$/, ""),
                  value:
                    activeType.value ??
                    ((r) => r[activeType.key] || "(none)"),
                }
          }
        />
      </div>

      <DownloadProgressModal
        isOpen={isDownloading}
        progress={downloadProgress}
        fileName="User Query Report.pdf"
        onClose={() => setIsDownloading(false)}
      />

      {/* ---> HISTORY MODAL POPUP <--- */}
      <Modal
        show={isHistoryModalOpen}
        onClose={closeHistoryModal}
        title="Task History Timeline"
        maxWidth="750px"
      >
        <div className="p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {selectedHistoryTask && (
            <div className="mb-4 pb-3 border-bottom d-flex justify-content-between align-items-start">
              <div>
                <h5 className="fw-bold text-primary mb-1">{selectedHistoryTask.TaskName || selectedHistoryTask.NatureofQuery}</h5>
                <div className="text-muted small">{selectedHistoryTask.FormName || selectedHistoryTask.ShortName}</div>
              </div>
              <span className="badge bg-light text-secondary border px-3 py-2">
                Query #{selectedHistoryTask.QueryNo || selectedHistoryTask.QueryCode}
              </span>
            </div>
          )}

          {historyStatus === "loading" ? (
            <div className="text-center py-5"><Loader isLoading={true} /></div>
          ) : (() => {
            const rawHistoryList = Array.isArray(historyData) ? historyData : historyData?.data || [];
            const historyList = [...rawHistoryList].sort((a, b) => {
              return new Date(b.ChangeTime) - new Date(a.ChangeTime);
            });

            return historyList.length > 0 ? (
              <div className="timeline-container ms-2 mt-4 pb-2">
                {historyList.map((h, i) => {
                  const statusColor = getTimelineColor(h.ChangeStatus);
                  const isLast = i === historyList.length - 1;

                  return (
                    <div key={i} className="timeline-item position-relative mb-4" style={{ paddingLeft: "2rem" }}>
                      {!isLast && (
                        <div
                          style={{
                            position: "absolute", left: "9px", top: "16px", bottom: "-24px",
                            width: "2px", backgroundColor: statusColor, zIndex: 1
                          }}
                        ></div>
                      )}
                      <div
                        className="timeline-dot position-absolute rounded-circle shadow-sm"
                        style={{
                          width: "14px", height: "14px", left: "3px", top: "4px",
                          border: "2px solid #fff", backgroundColor: statusColor, zIndex: 2
                        }}
                      ></div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>
                          Changed to: <span style={{ color: statusColor }}>{displayStatus(h.ChangeStatus)}</span>
                        </span>
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {formatDateTimeDisplay(h.ChangeTime)}
                        </span>
                      </div>
                      <div className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
                        By: <span className="fw-medium text-dark">{h.UserName}</span>
                      </div>
                      {h.Description && (
                        <div className="bg-light p-2 mt-2 rounded text-muted" style={{ fontSize: "0.8rem", borderLeft: `3px solid ${statusColor}` }}>
                          {h.Description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted text-center small py-5">
                <i className="bi bi-clock-history fs-3 d-block mb-2 text-light"></i>
                No history records found for this task yet.
              </div>
            );
          })()}
        </div>
      </Modal>

    </div>
  );
};

export default UserReports;